import React, { useEffect, useRef, useState } from "react";
import { Modal, Spin, Button } from "antd";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

// Schemes a link inside an uploaded document is allowed to keep. Anything else — javascript:,
// data:, vbscript:, protocol-relative — is stripped.
const SAFE_LINK_SCHEME = /^(?:https?:\/\/|mailto:|#)/i;

const base64ToBlob = (base64, mimeType) => {
    const raw = (base64.split(",")[1] ?? base64).replace(/\s/g, "");
    const byteChars = atob(raw);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
        byteArr[i] = byteChars.charCodeAt(i);
    }
    return new Blob([byteArr], { type: mimeType });
};

const base64ToBlobUrl = (base64, mimeType) => URL.createObjectURL(base64ToBlob(base64, mimeType));

// Reads the MIME type back out of a `data:<mime>;base64,...` source string.
const mimeFromDataUrl = (src) => {
    const match = /^data:([^;,]+)/.exec(src);
    return match ? match[1] : "application/octet-stream";
};

const downloadFile = (base64, mimeType, fileName) => {
    const blobUrl = base64ToBlobUrl(base64, mimeType);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

// docx-preview copies each hyperlink's target straight out of the document onto an <a href>
// with no scheme check, so a crafted upload could run script in this origin on a single click.
// Neutralise every link the renderer produced before the document becomes visible.
const hardenLinks = (container) => {
    const anchors = container.querySelectorAll("a[href]");
    for (let i = 0; i < anchors.length; i++) {
        const anchor = anchors[i];
        const href = (anchor.getAttribute("href") || "").trim();

        if (!SAFE_LINK_SCHEME.test(href)) {
            anchor.removeAttribute("href");
            anchor.style.cursor = "default";
        } else if (href.charAt(0) !== "#") {
            anchor.setAttribute("target", "_blank");
            anchor.setAttribute("rel", "noopener noreferrer");
        }
    }
};

const DownloadPrompt = ({ base64, mimeType, fileName, text, t }) => (
    <div style={{ textAlign: "center", padding: "24px 0" }}>
        <p>{text}</p>
        <Button type="primary" onClick={() => downloadFile(base64, mimeType, fileName)}>
            {t("tables.actions.download")}
        </Button>
    </div>
);

const DocxPreview = ({ base64, fileName, t }) => {
    const containerRef = useRef(null);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        let cancelled = false;

        const render = async () => {
            try {
                // Loaded on demand so docx-preview/jszip stay out of the main bundle.
                // Pinned to docx-preview 0.1.20: every later release ships optional chaining in its
                // dist and declares only "exports", neither of which the webpack 4 behind
                // react-scripts 3 can handle. Don't bump without re-checking the build.
                const docx = await import("docx-preview");
                const renderAsync = docx.renderAsync ?? docx.default?.renderAsync;
                if (cancelled || !containerRef.current) return;

                await renderAsync(base64ToBlob(base64, DOCX_MIME), containerRef.current, null, {
                    className: "docx",
                    inWrapper: true,
                    breakPages: true,
                    ignoreLastRenderedPageBreak: true,
                    useBase64URL: true, // embedded images survive the modal closing
                });
                hardenLinks(containerRef.current);
                if (!cancelled) setStatus("done");
            } catch (error) {
                console.error("docx preview failed", error);
                if (!cancelled) setStatus("error");
            }
        };
        render();

        return () => {
            cancelled = true;
        };
    }, [base64]);

    if (status === "error") {
        return (
            <DownloadPrompt
                base64={base64}
                mimeType={DOCX_MIME}
                fileName={fileName}
                text={t("contents.alerts.preview_unavailable")}
                t={t}
            />
        );
    }

    return (
        <>
            {status === "loading" && (
                <div style={{ textAlign: "center", padding: "48px 0" }}>
                    <Spin />
                    <div style={{ marginTop: "8px", color: "#999" }}>{t("contents.alerts.preview_loading")}</div>
                </div>
            )}
            <div ref={containerRef} style={{ maxHeight: "70vh", overflow: "auto" }} />
        </>
    );
};

export const FileViewer = {
    open(name, ext, src, t) {
        const fileName = `${name}.${ext}`;
        const closeText = t("tables.actions.close");

        if (ext === "pdf") {
            const blobUrl = base64ToBlobUrl(src, "application/pdf");
            const tab = window.open(blobUrl, "_blank");
            if (!tab) {
                // Popup blocked — fall back to download
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000); // 60 s — give browser time to fully load
            return;
        }

        if (ext === "docx") {
            Modal.info({
                title: fileName,
                width: 960,
                icon: null,
                content: <DocxPreview base64={src} fileName={fileName} t={t} />,
                okText: closeText,
                centered: true,
            });
            return;
        }

        if (IMAGE_EXTENSIONS.includes(ext)) {
            Modal.info({
                title: fileName,
                width: 800,
                icon: null,
                content: <img src={src} alt="preview" style={{ width: "100%" }} />,
                okText: closeText,
                centered: true,
            });
            return;
        }

        // Legacy .doc and anything else browsers can't render — offer the file instead of a dead end.
        Modal.info({
            title: fileName,
            width: 480,
            icon: null,
            content: (
                <DownloadPrompt
                    base64={src}
                    mimeType={mimeFromDataUrl(src)}
                    fileName={fileName}
                    text={t("contents.alerts.preview_unsupported")}
                    t={t}
                />
            ),
            okText: closeText,
            centered: true,
        });
    },
};
